import { PlusSquare, Share, SquareCheckBig } from "lucide-react";
import type { ReactNode } from "react";

import { TWIDO_APP_NAME } from "@/branding";
import { usePwaInstall } from "@/context/PwaInstallContext";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

interface StepProps {
  icon: ReactNode;
  children: ReactNode;
}

function Step({ icon, children }: StepProps) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
        {icon}
      </span>
      <span className="text-sm text-foreground">{children}</span>
    </li>
  );
}

/**
 * iOS-only fallback for installing the PWA. Safari has no programmatic install,
 * so we explain the "Add to Home Screen" flow. Controlled by PwaInstallContext.
 */
export function PwaInstallInstructionsSheet() {
  const { instructionsOpen, closeInstallInstructions } = usePwaInstall();

  return (
    <Sheet
      open={instructionsOpen}
      onOpenChange={(open) => {
        if (!open) closeInstallInstructions();
      }}
    >
      <SheetContent side="bottom" className="mx-auto max-w-md rounded-t-2xl">
        <SheetHeader>
          <SheetTitle>Install {TWIDO_APP_NAME}</SheetTitle>
          <SheetDescription>
            Add {TWIDO_APP_NAME} to your home screen for a full-screen, app-like
            experience.
          </SheetDescription>
        </SheetHeader>
        <ol className="mt-5 flex flex-col gap-4">
          <Step icon={<Share className="size-4" />}>
            Tap the <span className="font-medium">Share</span> button in the
            Safari toolbar.
          </Step>
          <Step icon={<PlusSquare className="size-4" />}>
            Scroll down and tap{" "}
            <span className="font-medium">Add to Home Screen</span>.
          </Step>
          <Step icon={<SquareCheckBig className="size-4" />}>
            Tap <span className="font-medium">Add</span> — {TWIDO_APP_NAME} opens
            full-screen from your home screen.
          </Step>
        </ol>
      </SheetContent>
    </Sheet>
  );
}
